/**
 * Unfilled required fields, made visible (Pedro 2026-10-10: "do we have a visible way to
 * nevertheless highlight unfilled fields, so user can see and remember to fill?").
 *
 * The completeness rules decide what is required; they never block a save. On a detail
 * page, every field a rule flags as missing shows in three ways:
 *   - in the violet banner (IncompleteBanner), by its readable name, clickable to open its tab;
 *   - as a violet count on the tab that holds it (TabMissingCount);
 *   - as a violet outline on the empty input itself (`useMissingFields().input`). The outline
 *     goes as soon as the field is filled in the form; the banner and the counts follow on Save.
 * Violet is the app's colour for compliance data quality (app-language §2).
 *
 * FIELD_TABS is the ONE field → tab map per record type. It covers every field the field
 * registry offers for a rule, the computed checks, and the fields the pages validate on
 * save (their "jump to the tab" also reads it). A field it lacks still shows in the
 * banner, just without the jump.
 */
import { Badge, Tooltip } from '@mantine/core';

import type { CompletenessEntityType, UUID } from '../api/types';
import { useFieldsMissing } from '../hooks/useCompleteness';

export const FIELD_TABS: Record<CompletenessEntityType, Record<string, string>> = {
  PERSON: {
    display_name: 'identity', id_type: 'identity', id_number: 'identity',
    id_issuing_country: 'identity', id_issue_date: 'identity', id_expiry_date: 'identity',
    pep_status: 'identity', us_person: 'identity',
    kyc_last_date: 'kyc', kyc_next_date: 'kyc',
    email: 'contact', mobile_num: 'contact',
    gender: 'birth', birth_date: 'birth', nationality_1: 'birth', birth_place_country: 'birth',
    tin_1: 'fiscal', fiscal_country_1: 'fiscal', _tin_1_valid_pt_nif: 'fiscal',
    professional_status: 'profession', profession: 'profession', employer: 'profession',
    marital_status: 'marital',
    legal_form: 'legal', lei: 'legal', nipc: 'legal', incorporation_country: 'legal',
    incorporation_date: 'legal', regulatory_authority: 'legal', tin: 'legal',
    // P16b (migration 0105): the client-record fields.
    given_names: 'identity', surnames: 'identity',
    financial_situation: 'kyc', source_of_wealth: 'kyc', expected_flows: 'kyc',
    tax_obligations_met: 'kyc',
    children_count: 'marital', children: 'marital',
    corporate_purpose: 'legal', cae_code: 'legal', listed_company: 'legal', listed_market: 'legal',
    high_risk_countries_declaration: 'legal', ownership_structure: 'legal',
  },
  AGREEMENT: {
    client_mnemonic: 'identity', client_number: 'identity',
    mifid_investor_nature: 'kyc', mifid_residence: 'kyc', mifir_id: 'kyc', mifir_id_type: 'kyc',
    kyc_last_date: 'kyc', kyc_next_date: 'kyc',
    _has_iaa_initial: 'kyc', _iaa_signed_dates_complete: 'kyc',
    mifid_suitability_score: 'mifid', strategic_allocation: 'mifid',
    mifid_suitability_last_date: 'mifid', mifid_suitability_next_date: 'mifid',
    _allocation_within_suitability: 'mifid',
    aml_risk: 'aml', aml_last_date: 'aml', aml_next_date: 'aml',
    _billing_identities_complete: 'fees',
  },
  ACCOUNT: {
    agreement_id: 'bank', opened_by_policy_id: 'bank',
    bank_id: 'bank', bank_name: 'bank', internal_num: 'bank', rto_approved: 'bank', iban: 'bank',
    swift_bic: 'bank', _iban_valid_iso13616: 'bank',
    bank_address: 'address',
    contact_name: 'contact', contact_phone: 'contact', contact_email: 'contact',
  },
  INSURANCE_POLICY: {
    insurer: 'identity', policy_number: 'identity', fund_type: 'identity', fund_number: 'identity',
    _has_policy_holder: 'parties', _has_life_insured: 'parties', _has_beneficiary: 'parties',
    _has_account: 'accounts',
  },
};

const isEmpty = (v: unknown) => v === '' || v === null || v === undefined;
/** A multi-line block is empty while it holds no value at all (blank lines count as empty). */
const isBlankDeep = (v: unknown): boolean => {
  if (isEmpty(v)) return true;
  if (Array.isArray(v)) return v.every(isBlankDeep);
  if (typeof v === 'object') return Object.values(v as object).every(isBlankDeep);
  return false;
};

// A computed check that is about ONE input: the input is outlined even though it is filled,
// because its value fails the check (smoke 2026-10-11, S1).
const CHECK_INPUT: Record<string, string> = {
  _iban_valid_iso13616: 'iban',
  _tin_1_valid_pt_nif: 'tin_1',
  _allocation_within_suitability: 'strategic_allocation',
};

// Smoke 2026-10-11 (S1, Pedro: "too faint"): a 2px violet border on a violet tint, kept at
// full opacity — in Read mode the inputs are disabled and Mantine dims them, which made the
// former 1px colour change nearly invisible.
const STRONG_INPUT = {
  borderColor: 'var(--mantine-color-violet-6)', borderWidth: 2,
  backgroundColor: 'var(--mantine-color-violet-0)', opacity: 1,
};

/** The record's missing required fields, with a count per tab and the input outline. */
export function useMissingFields(entityType: CompletenessEntityType, id: UUID | undefined) {
  const q = useFieldsMissing(entityType, id);
  const missing = q.data?.missing_fields ?? [];
  const tabCounts: Record<string, number> = {};
  for (const f of missing) {
    const tab = FIELD_TABS[entityType][f];
    if (tab) tabCounts[tab] = (tabCounts[tab] ?? 0) + 1;
  }
  const failsCheck = (field: string) => missing.some((m) => CHECK_INPUT[m] === field);
  /** Spread onto an input: the violet outline while the field is missing on record and
   *  still empty in the form, or while a check on its value fails (until the next Save). */
  const input = (field: string, value: unknown) =>
    ((missing.includes(field) && isEmpty(value)) || failsCheck(field)
      ? { styles: { input: STRONG_INPUT } }
      : {});
  /** The same outline for a multi-line block (a Fieldset): P16b's source of wealth,
   *  expected flows… — missing on record and still empty in the form. */
  const block = (field: string, value: unknown) =>
    (missing.includes(field) && isBlankDeep(value)
      ? { style: { borderColor: 'var(--mantine-color-violet-6)', borderWidth: 2,
          backgroundColor: 'var(--mantine-color-violet-0)' } }
      : {});
  return { missing, tabCounts, input, block };
}

/** A tab's count of missing required fields (nothing when there are none). */
export function TabMissingCount({ count }: { count?: number }) {
  if (!count) return null;
  return (
    <Tooltip label={`${count} required field${count === 1 ? '' : 's'} missing on this tab`} withArrow>
      <Badge size="xs" circle color="violet" variant="filled">{count}</Badge>
    </Tooltip>
  );
}
